import React, { useMemo, useState } from 'react'
import { Spinner, Modal } from 'react-bootstrap'
import styled from 'styled-components'
import { Model } from '../../../../types/models/model'
import { Content } from '../../../../types/models/content'
import { useFetchAnnotatedContentQuery } from '../../../../services/contentApi'
import { useAnnotationStats } from '../../../../store/hooks'
import { useAppSelector } from '../../../../store/hooks'
import { selectSelectedProjectId } from '../../../../store/slices/projectSlice'
import ContentImageComponent from '../../../tools/ContentImage'
import { FaImages, FaDatabase, FaChartLine, FaEye, FaRobot, FaArrowRight } from 'react-icons/fa'

// Active Labeling Results Layout
const ComparisonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  min-height: 0;
  padding: 1rem;
`

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 1.5rem;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`

const ResultsHeader = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(52, 211, 153, 0.1));
  border: 2px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  padding: 1.5rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #6366f1, #34d399);
  }
`

const ResultsTitle = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const ResultsStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`

const StatCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
`

const StatValue = styled.div<{ $highlight?: boolean }>`
  color: ${(props) => (props.$highlight ? '#34d399' : 'white')};
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.5rem;
`

const StatLabel = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const PerformanceChange = styled.div<{ $improved?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${(props) =>
    props.$improved ? '#34d399' : props.$improved === false ? '#f87171' : '#9ca3af'};
  font-size: 0.9rem;
  font-weight: 600;
`

const NewDataSection = styled.div`
  flex: 1;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  border: 2px solid rgba(52, 211, 153, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`

const NewDataHeader = styled.div`
  background: rgba(52, 211, 153, 0.2);
  padding: 0.75rem 1rem;
  border-bottom: 2px solid rgba(52, 211, 153, 0.3);
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const NewDataTitle = styled.h3`
  color: #34d399;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const DataContext = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-end;
`

const ContextBadge = styled.div<{ $type?: 'source' | 'model' | 'target' }>`
  background: ${(props) => {
    switch (props.$type) {
      case 'source':
        return 'rgba(59, 130, 246, 0.3)'
      case 'model':
        return 'rgba(168, 85, 247, 0.3)'
      case 'target':
        return 'rgba(16, 185, 129, 0.3)'
      default:
        return 'rgba(107, 114, 128, 0.3)'
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$type) {
        case 'source':
          return 'rgba(59, 130, 246, 0.5)'
        case 'model':
          return 'rgba(168, 85, 247, 0.5)'
        case 'target':
          return 'rgba(16, 185, 129, 0.5)'
        default:
          return 'rgba(107, 114, 128, 0.5)'
      }
    }};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const ImageGridArea = styled.div`
  flex: 1;
  min-height: 0;
  padding: 1.5rem;
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(52, 211, 153, 0.5);
    border-radius: 4px;
  }
`

const ActiveLabelingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1rem;

  @media (min-width: 1400px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
`

const ImageCard = styled.div`
  aspect-ratio: 1;
  width: 100%;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    border-color: rgba(52, 211, 153, 0.8);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  gap: 1rem;
`

const ControlsSection = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 0 1.5rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`

const DataTypeToggle = styled.button<{ $active?: boolean }>`
  background: ${(props) => (props.$active ? 'rgba(52, 211, 153, 0.5)' : 'transparent')};
  border: 1px solid rgba(52, 211, 153, 0.5);
  border-radius: 8px;
  color: ${(props) => (props.$active ? 'white' : 'rgba(255, 255, 255, 0.8)')};
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(52, 211, 153, 0.3);
    color: white;
  }
`

interface DatasetComparisonViewProps {
  model1: Model | undefined
  model2: Model | undefined
}

const DatasetComparisonView: React.FC<DatasetComparisonViewProps> = ({ model1, model2 }) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const [selectedView, setSelectedView] = useState<'training-diff' | 'validation-diff'>(
    'training-diff'
  )
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20
  const [selectedImage, setSelectedImage] = useState<Content | null>(null)
  const [showImageModal, setShowImageModal] = useState(false)

  // Get annotation stats for both models
  const { data: annoStats1 } = useAnnotationStats(
    selectedProjectId || '',
    model1?.datasetid || '',
    model1?.parameters?.train_metadata?.datasource || []
  )

  const { data: annoStats2 } = useAnnotationStats(
    selectedProjectId || '',
    model2?.datasetid || '',
    model2?.parameters?.train_metadata?.datasource || []
  )

  // Calculate dataset differences
  const datasetDifferences = useMemo(() => {
    const model1TrainingSources = new Set(model1?.parameters?.train_metadata?.datasource || [])
    const model1ValidationSources = new Set(
      model1?.parameters?.validation_metadata?.datasource || []
    )
    const model2TrainingSources = new Set(model2?.parameters?.train_metadata?.datasource || [])
    const model2ValidationSources = new Set(
      model2?.parameters?.validation_metadata?.datasource || []
    )

    const sharedTrainingSources = [...model1TrainingSources].filter((x) =>
      model2TrainingSources.has(x)
    )
    const sharedValidationSources = [...model1ValidationSources].filter((x) =>
      model2ValidationSources.has(x)
    )

    const uniqueToModel1Training = [...model1TrainingSources].filter(
      (x) => !model2TrainingSources.has(x)
    )
    const uniqueToModel2Training = [...model2TrainingSources].filter(
      (x) => !model1TrainingSources.has(x)
    )
    const uniqueToModel1Validation = [...model1ValidationSources].filter(
      (x) => !model2ValidationSources.has(x)
    )
    const uniqueToModel2Validation = [...model2ValidationSources].filter(
      (x) => !model1ValidationSources.has(x)
    )

    return {
      sharedTrainingSources,
      sharedValidationSources,
      uniqueToModel1Training,
      uniqueToModel2Training,
      uniqueToModel1Validation,
      uniqueToModel2Validation,
    }
  }, [model1, model2])

  const { data: uniqueImages2, isLoading: loading2 } = useFetchAnnotatedContentQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: model2?.datasetid || '',
      page: currentPage - 1,
      limit: itemsPerPage,
      metadata_filter: {
        datasource:
          selectedView === 'training-diff'
            ? datasetDifferences.uniqueToModel2Training
            : datasetDifferences.uniqueToModel2Validation,
      },
      operator: 'and',
      sort_key: 'updated_at',
      sort_val: -1,
    },
    {
      skip:
        !selectedProjectId ||
        !model2?.datasetid ||
        (selectedView === 'training-diff' &&
          datasetDifferences.uniqueToModel2Training.length === 0) ||
        (selectedView === 'validation-diff' &&
          datasetDifferences.uniqueToModel2Validation.length === 0),
    }
  )

  const handleImageClick = (image: Content) => {
    setSelectedImage(image)
    setShowImageModal(true)
  }

  return (
    <ComparisonContainer>
      {!model1 || !model2 ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '1rem',
          }}
        >
          Select two models to compare their datasets
        </div>
      ) : (
        <>
          {/* Main Content Grid */}
          <MainContent>
            <ResultsHeader>
              <ResultsTitle>
                <FaDatabase />
                Active Labeling Results
              </ResultsTitle>
              <ControlsSection>
                <DataTypeToggle
                  $active={selectedView === 'training-diff'}
                  onClick={() => {
                    setSelectedView('training-diff')
                    setCurrentPage(1)
                  }}
                >
                  Training Data
                </DataTypeToggle>
                <DataTypeToggle
                  $active={selectedView === 'validation-diff'}
                  onClick={() => {
                    setSelectedView('validation-diff')
                    setCurrentPage(1)
                  }}
                >
                  Validation Data
                </DataTypeToggle>
              </ControlsSection>
              <ResultsStats>
                <StatCard>
                  <StatValue>{annoStats1?.statistics?.total_annotated_images || 0}</StatValue>
                  <StatLabel>Base Model Training Images</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue $highlight>+{uniqueImages2?.content?.length || 0}</StatValue>
                  <StatLabel>New Images Added</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{annoStats2?.statistics?.total_annotated_images || 0}</StatValue>
                  <StatLabel>Total Training Images</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue $highlight>
                    +
                    {Math.round(
                      ((uniqueImages2?.content?.length || 0) /
                        Math.max(1, annoStats1?.statistics?.total_annotated_images || 1)) *
                        100
                    )}
                    %
                  </StatValue>
                  <StatLabel>Dataset Growth</StatLabel>
                </StatCard>
              </ResultsStats>
              <PerformanceChange
                $improved={uniqueImages2?.content && uniqueImages2.content.length > 0}
              >
                <FaChartLine />
                {uniqueImages2?.content && uniqueImages2.content.length > 0
                  ? `Active labeling added ${uniqueImages2.content.length} new training images`
                  : 'No new training data was added in this iteration'}
              </PerformanceChange>
            </ResultsHeader>
            {/* Column 1: Results Header */}

            {/* Column 2: New Data Section */}
            <NewDataSection>
              <NewDataHeader>
                <NewDataTitle>
                  <FaImages />
                  New Training Data ({uniqueImages2?.content?.length || 0} images)
                </NewDataTitle>
                <DataContext>
                  <ContextBadge $type="model">
                    <FaRobot />
                    {model1?.name}
                  </ContextBadge>
                  <ContextBadge $type="source">
                    <FaDatabase />
                    {model2?.parameters?.train_metadata?.datasource
                      ?.filter(
                        (source) =>
                          !model1?.parameters?.train_metadata?.datasource?.includes(source)
                      )
                      .join(', ') || 'Unknown'}
                  </ContextBadge>
                  <ContextBadge $type="target">
                    <FaArrowRight />
                    {model2?.name}
                  </ContextBadge>
                </DataContext>
              </NewDataHeader>

              <ImageGridArea>
                {loading2 ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '300px',
                    }}
                  >
                    <Spinner animation="border" variant="light" />
                  </div>
                ) : uniqueImages2?.content && uniqueImages2.content.length > 0 ? (
                  <ActiveLabelingGrid>
                    {uniqueImages2.content.map((image: Content) => (
                      <ImageCard key={image.id} onClick={() => handleImageClick(image)}>
                        <ContentImageComponent
                          content={image}
                          onImageClick={() => handleImageClick(image)}
                          selectable={false}
                        />
                      </ImageCard>
                    ))}
                  </ActiveLabelingGrid>
                ) : (
                  <EmptyState>
                    <FaImages size={48} />
                    <h4 style={{ color: 'white', margin: '0.5rem 0' }}>
                      No new training data found
                    </h4>
                    <p style={{ margin: 0, opacity: 0.8 }}>
                      The iteration model has not added any new images to the training dataset from
                      active labeling.
                    </p>
                  </EmptyState>
                )}
              </ImageGridArea>
            </NewDataSection>
          </MainContent>

          {/* Image Detail Modal */}
          <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered size="lg">
            <Modal.Header
              closeButton
              style={{
                background: 'transparent',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Modal.Title style={{ color: 'white' }}>
                <FaEye style={{ marginRight: '0.5rem' }} />
                Training Image Details
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ background: 'transparent', padding: 0 }}>
              {selectedImage && (
                <div
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    position: 'relative',
                  }}
                >
                  <ContentImageComponent
                    content={selectedImage}
                    onImageClick={() => setShowImageModal(false)}
                    selectable={false}
                  />
                </div>
              )}
            </Modal.Body>
          </Modal>
        </>
      )}
    </ComparisonContainer>
  )
}

export default DatasetComparisonView
